import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';

import AssetList from '../components/AssetList';
import { useAdministrationAPI } from '../AdministrationAPI';

import { OperatingState } from '@the-real-insight/tri-model';
import { useModalContext } from '../components/ModalProvider';
import { useFormats } from '../hooks/formats';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface Properties {
}

export default function OperationsDashboard(_properties: Properties) {
    const { t } = useTranslation();
    const { numberFormat1Digit } = useFormats();
    const { pushError: _pushError } = useModalContext() as any;
    const { getLatestOperationalState: getLatestOperatingState } = useAdministrationAPI();
    const [operatingStates, setOperatingStates] = useState({}) as any;
    const [allAssets, setAllAssets] = useState([]) as any;
    const [resultsCapped, setResultsCapped] = useState(false);
    const [selectedState, setSelectedState] = useState<OperatingState | null>(null);
    const [nextRefresh, setNextRefresh] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);

    const fetch = async () => {
        setRefreshing(true);

        try {
            // Server returns { total, counts, capped, assets }: counts are over
            // ALL devices; assets is a bounded list (actionable devices in full
            // plus a normal sample) so the client never renders 100k rows.
            const result = await getLatestOperatingState();
            const counts = result?.counts ?? {};

            setAllAssets(result?.assets ?? []);
            setResultsCapped(result?.capped ?? false);

            setOperatingStates({
                total: result?.total ?? 0,
                normal: counts.normal ?? 0,
                warning: counts.warning ?? 0,
                critical: counts.critical ?? 0,
                maintenance: counts.maintenance ?? 0,
                decommisioned: counts.decommisioned ?? 0,
            });
        } catch (error) {
            console.log(error);
        } finally {
            setRefreshing(false);
            setLoadedOnce(true);
            setNextRefresh(60);
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            const newNextRefresh = nextRefresh - 5;

            if (newNextRefresh <= 0) {
                setNextRefresh(50);
                fetch();
            } else {
                setNextRefresh(newNextRefresh);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [nextRefresh]);

    useEffect(() => {
        fetch();
    }, []);

    const filteredAssets = useMemo(() => {
        // Check for null/undefined explicitly (not falsy, because 0 is falsy but valid)
        if (selectedState === null || selectedState === undefined) {
            return [];
        }

        // Convert selectedState (enum) to number for comparison
        // Server always returns operatingState as an integer
        const selectedStateNumber = Number(selectedState);

        return allAssets.filter((asset: any) => {
            // Server returns operatingState as an integer, so convert to number for comparison
            const assetStateNumber = Number(asset.operatingState);

            // Simple numeric comparison
            return assetStateNumber === selectedStateNumber;
        });
    }, [selectedState, allAssets]);

    const handleStateClick = (state: OperatingState) => {
        if (selectedState === state) {
            setSelectedState(null);
        } else {
            setSelectedState(state);
        }
    };

    const isStateSelected = (state: OperatingState) => {
        // Simple numeric comparison - server always returns integers
        if (selectedState === null || selectedState === undefined) {
            return false;
        }
        // Convert both to numbers and compare
        return Number(selectedState) === Number(state);
    };

    // Reihenfolge und Farbgebung wie im Bestand; die Zahlen bleiben Anteile an
    // `total`, die Beschriftung nennt die absolute Anzahl.
    const stateTiles = [{
        state: OperatingState.critical,
        count: operatingStates.critical,
        label: t('settingsPanel.settingsPanel.operationsDashboard.devicesWithCriticalIssues'),
        tone: 'text-destructive',
    }, {
        state: OperatingState.warning,
        count: operatingStates.warning,
        label: t('settingsPanel.settingsPanel.operationsDashboard.devicesWithWarnings'),
        tone: 'text-warning',
    }, {
        state: OperatingState.normal,
        count: operatingStates.normal,
        label: t('settingsPanel.settingsPanel.operationsDashboard.devicesNormal'),
        tone: 'text-success',
    }, {
        state: OperatingState.decommisioned,
        count: operatingStates.decommisioned,
        label: t('settingsPanel.settingsPanel.operationsDashboard.devicesDecommissioned'),
        tone: 'text-purple-700',
    }, {
        state: OperatingState.maintenance,
        count: operatingStates.maintenance,
        label: t('settingsPanel.settingsPanel.operationsDashboard.devicesInMaintenance'),
        tone: 'text-blue-700',
    }];

    return <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
            <p role="status" className="text-xs text-muted-foreground">
                {refreshing
                    ? t('settingsPanel.settingsPanel.operationsDashboard.refreshing')
                    : `${t('settingsPanel.settingsPanel.operationsDashboard.nextRefreshIn')} ${nextRefresh} ${t('settingsPanel.settingsPanel.operationsDashboard.seconds')}`}
            </p>
            <Button
                variant="ghost"
                size="icon"
                disabled={refreshing}
                aria-label={t('global.refresh')}
                title={t('global.refresh')}
                onClick={() => {
                    fetch();
                }}
            >
                <RefreshCw aria-hidden className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </Button>
        </div>
        {loadedOnce
            ?
            <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
                {stateTiles.map((tile) => <button
                    key={String(tile.state)}
                    type="button"
                    aria-pressed={isStateSelected(tile.state)}
                    onClick={() => handleStateClick(tile.state)}
                    className={cn(
                        'flex min-w-0 flex-col items-center rounded-md p-4 text-center transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        isStateSelected(tile.state) && 'bg-accent',
                    )}
                >
                    <span className={cn('font-bold', tile.tone)}>
                        <span className="text-[clamp(2rem,5vw,3.5rem)] leading-none">
                            {operatingStates.total ? numberFormat1Digit.format(100 * (tile.count ?? 0) / operatingStates.total) : '-'}
                        </span>
                        <span className="text-xl">%</span>
                    </span>
                    <span className={cn('mt-2 min-w-0 break-words text-sm', tile.tone)}>
                        {tile.count ?? '-'} {tile.label}
                    </span>
                </button>)}
            </div>
            :
            <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
                {stateTiles.map((tile) => <div key={String(tile.state)} className="flex min-w-0 flex-col items-center gap-2 p-4">
                    <Skeleton className="h-12 w-24" />
                    <Skeleton className="h-4 w-full" />
                </div>)}
            </div>}
        <div className="mt-6 flex min-w-0 flex-col gap-4">
            {selectedState === null
                ?
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-6 text-center">
                    <div className="text-sm font-semibold text-foreground">
                        {t('settingsPanel.settingsPanel.operationsDashboard.noStateSelectedTitle')}
                    </div>
                    <div className="max-w-prose text-sm text-muted-foreground">
                        {t('settingsPanel.settingsPanel.operationsDashboard.noStateSelectedHint')}
                    </div>
                </div>
                :
                <AssetList iotOnly assets={filteredAssets} resultsCapped={resultsCapped}></AssetList>}
        </div>
    </div>;
}
